import { Palette, PaletteColor, PaletteOptions } from '@mui/material'
import { every, isEmpty, isNil, isPlainObject, isString, set } from 'lodash'

import {
  DEFAULT_STALE_TIME,
  HEX_COLOR_REGEX,
  HEY_LIBBY_SITES,
} from '~/constants'
import { EAgentKeys, TAgent } from '~/features/agents/models'
import {
  EBrandColorType,
  EPartnerDomains,
  ESiteImagesTypes,
  TBrandColor,
  TPartner,
} from '~/features/partners/models'
import { EClientPath, EHttpService, EServerPath } from '~/models/api'
import EEnvironmentVariables from '~/models/config'
import HttpService from '~/services/http'
import pathBuilder from '~/utils/api'

export const queryKeys = {
  partnersByOrgId: (orgId: string) => [EServerPath.partnersByOrgs, { orgId }],
  partnersByDomain: (domain: string) => [EServerPath.partners, { domain }],
}

const domainMapping: { [key: string]: EPartnerDomains } = {
  localhost: EPartnerDomains.staging,
  'chrisannapoc.com': EPartnerDomains.whiteLabelChris,
  'develop.d1yqa48wwh6uoh.amplifyapp.com': EPartnerDomains.staging,
  'heylibby.co': EPartnerDomains.prod,
  'heylibby.net': EPartnerDomains.prod,
  'main.d3tfrvso00g6f0.amplifyapp.com': EPartnerDomains.prod,
}

export const getCorrespondentDomain = (): EPartnerDomains | string => {
  const normalizedUrl = window.location.hostname.replace(/^www\./, '')
  if (domainMapping[normalizedUrl]) {
    return domainMapping[normalizedUrl]
  }

  if (
    normalizedUrl.includes('localhost') ||
    normalizedUrl.includes('ngrok-free.app')
  ) {
    // to ensure it works when simulating partner behavior locally: by running the app from a subdomain, e.g. http://ggg.localhost:3000
    return domainMapping.localhost
  }

  return normalizedUrl
}

export const convertBrandColorsToPaletteObject = (
  brandColors: TBrandColor[],
) => {
  const colors = brandColors.reduce(
    (colorAccumulator: Palette, colorItem: TBrandColor) => {
      if (colorItem.type === EBrandColorType.errorMain) {
        return {
          ...colorAccumulator,
          error: { main: colorItem.color } as PaletteColor,
        }
      }

      if (colorItem.type.includes('.')) {
        set(colorAccumulator, colorItem.type, colorItem.color)
        return colorAccumulator
      }

      return { ...colorAccumulator, [colorItem.type]: colorItem.color }
    },
    {} as Palette,
  )

  return { palette: colors }
}

type NestedPalletteObject = { [key: string]: string | NestedPalletteObject }

const getPalletteObjectAsTBrandColors = (
  obj: NestedPalletteObject,
  parentKey: string = '',
): TBrandColor[] => {
  const result: TBrandColor[] = []

  Object.entries(obj).forEach(([key, value]) => {
    const currentKey = parentKey ? `${parentKey}.${key}` : key

    if (typeof value === 'object' && value !== null) {
      result.push(...getPalletteObjectAsTBrandColors(value, currentKey))
    } else {
      result.push({ type: currentKey, color: value as string } as TBrandColor)
    }
  })

  return result
}

export const convertPaletteObjectToBrandColors = (
  palette: PaletteOptions,
): TBrandColor[] => {
  const result = Object.entries(palette)
    .filter(([key]) => key !== 'common')
    .reduce<TBrandColor[]>((acc, [key, value]) => {
      if (typeof value === 'string') {
        return [
          ...acc,
          {
            type: key as EBrandColorType,
            color: value,
          } as TBrandColor,
        ]
      }

      if (
        Object.entries(value).every(
          ([keyInsideValue, valueInsideValue]: [string, unknown]) =>
            keyInsideValue === 'error' &&
            Object.keys(valueInsideValue as object).length === 1 &&
            !!(valueInsideValue as PaletteColor).main,
        )
      ) {
        return [
          ...acc,
          {
            type: EBrandColorType.errorMain,
            color: (value as PaletteColor).main,
          } as TBrandColor,
        ]
      }

      return [
        ...acc,
        ...getPalletteObjectAsTBrandColors(value as NestedPalletteObject, key),
      ]
    }, [])

  return result
}

export const isArrayOfTypeTBrandColor = (
  array: TBrandColor[] | null,
): boolean => {
  if (isEmpty(array) || isNil(array)) {
    return false
  }

  return every(array, (item) => {
    const result =
      isPlainObject(item) &&
      isString(item.type) &&
      isString(item.color) &&
      HEX_COLOR_REGEX.test(item.color)

    if (!result) {
      throw new Error(`Invalid brand color object: ${JSON.stringify(item)}`)
    }

    return result
  })
}

export const isLibbyDomain = () => {
  const currentHost = window.location.host.replace('www.', '')
  const isOnLocalhost = window.location.hostname.includes('localhost')
  const isOnSubdomain = window.location.hostname.split('.').length > 1
  const isSimulatingPartnerLocally = isOnLocalhost && isOnSubdomain

  return (
    HEY_LIBBY_SITES.includes(currentHost) ||
    (isOnLocalhost && !isSimulatingPartnerLocally)
  )
}

export const getImgUrlByType = (type: ESiteImagesTypes, partner: TPartner) => {
  return (
    partner?.siteImages?.find((image) => image.type === type)?.imageUrl || ''
  )
}

export const shouldDisplayVgLogin = () => {
  if (!import.meta.env[EEnvironmentVariables.VITE_OIDC_NAME]) {
    return false
  }

  const isOnLocalhost = window.location.hostname.includes('localhost')
  const isOnSubdomain = window.location.hostname.split('.').length > 1
  const isSimulatingPartnerLocally = isOnLocalhost && isOnSubdomain

  if (isSimulatingPartnerLocally) {
    return false // change this according to your local development needs.
  }

  return [
    EPartnerDomains.virtualGurus,
    EPartnerDomains.whiteLabelChris,
  ].includes(window.location.hostname.replace(/^www\./, '') as EPartnerDomains)
}

export const queries = {
  partnerByOrgId: (orgId: string = '') => ({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKeys.partnersByOrgId(orgId),
    queryFn: () =>
      HttpService.get<TPartner>({
        path: EServerPath.partnersByOrgs,
        query: { orgId },
      }),
    enabled: !!orgId,
    staleTime: DEFAULT_STALE_TIME,
  }),
  partnerByDomain: (enabled: boolean) => {
    const domain = getCorrespondentDomain()

    return {
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: queryKeys.partnersByDomain(domain),
      queryFn: () =>
        HttpService.get<TPartner>(
          {
            path: EServerPath.partners,
            query: { domain },
          },
          EHttpService.partner,
        ),
      staleTime: DEFAULT_STALE_TIME,
      enabled: enabled && !!domain,
    }
  },
}

export type TGetChatUrlProps = {
  agentChatFriendlyUrl: TAgent[EAgentKeys.chatFriendlyUrl]
  agentId: TAgent[EAgentKeys.id]
  partner?: TPartner
}

export const getChatUrl = ({
  agentChatFriendlyUrl,
  agentId,
  partner,
}: TGetChatUrlProps) => {
  const baseDomain = partner?.chatbotDomain || getCorrespondentDomain()
  const base = `https://${baseDomain}`

  if (agentChatFriendlyUrl) {
    return `${base}${pathBuilder(EClientPath.chat, { friendlyUrl: agentChatFriendlyUrl })}`
  }

  return `${base}${pathBuilder(EClientPath.oldChat, { agentId })}`
}
